import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Compass, CheckCircle2, Circle, ScrollText, Loader2, Feather, ChevronLeft, Download, Library } from "lucide-react";

const LEVEL_TINT = "text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#F3EEFA] text-[#59288E]";
const LEVEL_LABELS = ["Who Are They?", "Understanding the Records", "Culture", "Government & Political History", "Language Foundations", "Researching Your Family"];

function downloadReportPdf(report, tribeName) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const marginX = 56;
  const maxW = 500;
  let y = 64;
  const title = `${report.tribe || tribeName} — Level 1–6 Research Report`;
  doc.setFont("times", "bold"); doc.setFontSize(20); doc.setTextColor("#180D24");
  doc.text(doc.splitTextToSize(title, maxW), marginX, y); y += 28;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#8F7220");
  doc.text("WhatsMyTribe.com — Heritage Portal • Powered by ISelfLawAmMaster.com", marginX, y); y += 24;
  (report.levels || []).forEach((lvl, idx) => {
    if (y > 700) { doc.addPage(); y = 64; }
    doc.setFont("times", "bold"); doc.setFontSize(13); doc.setTextColor("#59288E");
    const head = `Level ${lvl.level || idx + 1} — ${lvl.title || LEVEL_LABELS[idx] || ""}`;
    doc.text(doc.splitTextToSize(head, maxW), marginX, y); y += 18;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor("#1A171D");
    const lines = doc.splitTextToSize((lvl.body || "").trim(), maxW);
    lines.forEach((ln) => {
      if (y > 740) { doc.addPage(); y = 64; }
      doc.text(ln, marginX, y); y += 14;
    });
    y += 12;
  });
  if (y > 700) { doc.addPage(); y = 64; }
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor("#B45309");
  doc.text(doc.splitTextToSize("This educational report is for research and learning. Community history, records or language study does not by itself establish citizenship, enrollment or membership — each nation sets its own requirements.", maxW), marginX, y);
  const safe = (report.tribe || tribeName || "tribal").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`${safe}-level-1-6-report.pdf`);
}
export default function TribalPaths() {
  const [paths, setPaths] = useState(null);
  const [open, setOpen] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [saved, setSaved] = useState([]);
  const loadSaved = () => api.get("/tribal-path-reports").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { api.get("/tribal-paths").then((r) => setPaths(r.data)); loadSaved(); }, []);
  if (!paths) return <Loader />;

  const closeModal = () => { setOpen(null); setReport(null); setReportLoading(false); loadSaved(); };

  const openPath = async (p) => {
    setOpen(p); setReport(null); setReportLoading(false);
    try {
      const { data } = await api.get("/tribal-path-report", { params: { tribe: p.name } });
      if (data && data.saved) setReport(data);
    } catch { /* no saved report yet */ }
  };

  const openSaved = (tribe) => openPath({ name: tribe, focus: "Your saved Level 1–6 report.", levels: LEVEL_LABELS });

  const generateReport = async () => {
    if (!open) return;
    setReportLoading(true); setReport(null);
    try {
      const { data } = await api.post("/tribal-path-report", { tribe: open.name });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the Level 1–6 report");
    } finally { setReportLoading(false); }
  };

  return (
    <div data-testid="module-tribal-paths">
      <PageHeader
        eyebrow="Understand"
        title="Tribal Research Paths"
        subtitle="These paths are drawn directly from your Tribal Affiliation report — the primary affiliation and the tribes historically prevalent in your matrilineal ancestor's region. They teach records and history, and distinguish community association from formal citizenship."
      />
      {paths.length === 0 ? (
        <Panel className="p-10 text-center text-[#756D7E]" data-testid="tribal-paths-empty">
          <Feather size={30} className="text-[#D4AF37] mx-auto mb-3" />
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-1">No research paths yet</h3>
          <p className="text-sm max-w-lg mx-auto">Your research paths appear here once you generate your <span className="font-semibold text-[#59288E]">Tribal Affiliation report</span>. Open the <span className="font-semibold">Tribal Affiliation</span> page and click “Generate Affiliation Report” — the tribes it surfaces will populate this page automatically.</p>
        </Panel>
      ) : (
      <div className="grid md:grid-cols-2 gap-5">
        {paths.map((p, i) => (
          <Panel key={p.name} className="p-6 hover:border-[#D4AF37] transition-colors cursor-pointer hp-fade-up"
            style={{ animationDelay: `${i * 50}ms` }} data-testid={`tribal-path-${i}`} onClick={() => openPath(p)}>
            <div className="flex items-start justify-between mb-3">
              <div className="h-11 w-11 rounded-xl bg-[#2A1442] grid place-items-center"><Compass size={20} className="text-[#D4AF37]" /></div>
              <span className={LEVEL_TINT}>{p.level}</span>
            </div>
            <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-1">{p.name}</h3>
            <p className="text-xs text-[#756D7E] mb-4">{p.focus}</p>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#59288E]">
              <ScrollText size={13} /> Open Level 1–6 report
            </div>
          </Panel>
        ))}
      </div>
      )}

      {saved.length > 0 && (
        <Panel className="p-6 mt-6" data-testid="saved-reports-library">
          <div className="flex items-center gap-2 mb-3 text-[#180D24] font-serif text-lg font-semibold">
            <Library size={18} className="text-[#59288E]" /> My Saved Reports
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {saved.map((s) => (
              <button key={s.tribe} data-testid={`saved-report-${s.tribe.slice(0, 16).replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => openSaved(s.tribe)}
                className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-[#E4DCD3] p-3 hover:border-[#D4AF37] transition-colors">
                <div className="min-w-0">
                  <div className="font-semibold text-[#180D24] text-sm truncate">{s.tribe}</div>
                  <div className="text-xs text-[#756D7E]">{s.levels} levels · saved {s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}</div>
                </div>
                <ScrollText size={16} className="text-[#D4AF37] shrink-0" />
              </button>
            ))}
          </div>
        </Panel>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tribal-path-dialog">
          {open && !report && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#180D24]">{open.name}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#756D7E]">{open.focus}</p>
              <div className="mt-3 space-y-2">
                {open.levels.map((lvl, idx) => {
                  const done = (idx / open.levels.length) * 100 < open.progress;
                  return (
                    <div key={lvl} className="flex items-center gap-3 rounded-xl border border-[#E4DCD3] p-3">
                      {done ? <CheckCircle2 size={18} className="text-[#10B981]" /> : <Circle size={18} className="text-[#9CA3AF]" />}
                      <span className="text-xs font-mono text-[#8F7220]">Level {idx + 1}</span>
                      <span className="text-sm text-[#180D24]">{lvl}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 rounded-xl bg-[#F3EEFA] p-4">
                <div className="flex items-start gap-3">
                  <Feather size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#3F1E68]">Generate a detailed, AI-researched Level 1–6 report on the {open.name} — covering who they are, the records to research, culture, government, language and how to trace your own family line.</p>
                </div>
                <button data-testid="generate-level-report-btn" onClick={generateReport} disabled={reportLoading}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
                  {reportLoading ? <Loader2 size={16} className="animate-spin" /> : <ScrollText size={16} className="text-[#E7C965]" />}
                  {reportLoading ? "Researching the six levels…" : "Generate Level 1–6 Report"}
                </button>
              </div>
            </>
          )}

          {open && report && (
            <div data-testid="tribal-path-report">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#180D24]">{report.tribe || open.name} — Level 1–6 Report</DialogTitle>
              </DialogHeader>
              <button onClick={() => setReport(null)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#59288E] hover:text-[#180D24]">
                <ChevronLeft size={14} /> Back to path overview
              </button>
              {report.saved && report.created_at && (
                <div className="mt-1 text-[11px] font-mono text-[#8F7220]" data-testid="report-saved-note">Saved report — {new Date(report.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button data-testid="download-report-pdf-btn" onClick={() => { try { downloadReportPdf(report, open.name); } catch (e) { toast.error("Could not build the PDF"); } }}
                  className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-4 py-2 text-sm font-bold hover:bg-[#E7C965] transition-colors">
                  <Download size={15} /> Download as PDF
                </button>
                <button data-testid="regenerate-report-btn" onClick={generateReport} disabled={reportLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] px-4 py-2 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors disabled:opacity-60">
                  {reportLoading ? <Loader2 size={15} className="animate-spin" /> : <ScrollText size={15} />} Regenerate
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {(report.levels || []).map((lvl, idx) => (
                  <div key={idx} className="rounded-xl border border-[#E4DCD3] bg-[#FAF8F5] p-4" data-testid={`report-level-${lvl.level || idx + 1}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={LEVEL_TINT}>Level {lvl.level || idx + 1}</span>
                      <h4 className="font-serif text-lg font-semibold text-[#180D24]">{lvl.title || LEVEL_LABELS[idx] || `Level ${idx + 1}`}</h4>
                    </div>
                    <div className="text-sm text-[#1A171D] leading-relaxed whitespace-pre-line">{lvl.body}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-[#FFFBEB] border border-[#F59E0B]/30 p-3 text-xs text-[#B45309]">
                This educational report is generated for research and learning. Community history, records or language study does not by itself establish citizenship, enrollment or membership — each nation sets its own requirements.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
